import { api } from "@/lib/api";

/**
 * Batch 21 — Executive Donor Intelligence tracker.
 *
 * Fire-and-forget, anonymous, privacy-preserving. Never awaited by the
 * caller; wrapped in try/catch so donation UX cannot be delayed or blocked.
 * Captures UTM/campaign attribution, origin-page attribution, browser/OS,
 * viewport, timezone and timing metrics — never PII, card details, PayPal
 * credentials, EcoCash PINs, passwords, or authentication tokens.
 */
const SESSION_KEY = "gsf_analytics_session";
const SESSION_STARTED_KEY = "gsf_analytics_session_started";
const UTM_KEY = "gsf_analytics_utm";
const ORIGIN_KEY = "gsf_analytics_origin";

function _sessionId() {
    try {
        let sid = sessionStorage.getItem(SESSION_KEY);
        if (!sid) {
            sid = (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)) + Date.now().toString(36);
            sessionStorage.setItem(SESSION_KEY, sid);
            sessionStorage.setItem(SESSION_STARTED_KEY, String(Date.now()));
        }
        return sid;
    } catch { return "anon-" + Date.now().toString(36); }
}

function _sessionAgeMs() {
    try {
        const started = Number(sessionStorage.getItem(SESSION_STARTED_KEY) || 0);
        if (!started) return 0;
        return Date.now() - started;
    } catch { return 0; }
}

function _deviceType() {
    if (typeof navigator === "undefined") return "unknown";
    const ua = (navigator.userAgent || "").toLowerCase();
    if (/ipad|tablet/.test(ua)) return "tablet";
    if (/mobi|android|iphone/.test(ua)) return "mobile";
    return "desktop";
}

function _browser() {
    if (typeof navigator === "undefined") return "unknown";
    const ua = navigator.userAgent || "";
    if (/edg\//i.test(ua)) return "Edge";
    if (/chrome\//i.test(ua) && !/opr\//i.test(ua)) return "Chrome";
    if (/firefox\//i.test(ua)) return "Firefox";
    if (/safari\//i.test(ua) && !/chrome\//i.test(ua)) return "Safari";
    if (/opr\//i.test(ua)) return "Opera";
    if (/samsungbrowser\//i.test(ua)) return "Samsung Internet";
    return "Other";
}

function _os() {
    if (typeof navigator === "undefined") return "unknown";
    const ua = navigator.userAgent || "";
    if (/windows/i.test(ua)) return "Windows";
    if (/android/i.test(ua)) return "Android";
    if (/iphone|ipad|ipod/i.test(ua)) return "iOS";
    if (/mac os x|macintosh/i.test(ua)) return "macOS";
    if (/linux/i.test(ua)) return "Linux";
    return "Other";
}

function _viewport() {
    try {
        return {
            w: window.innerWidth || 0,
            h: window.innerHeight || 0,
            dpr: Math.round((window.devicePixelRatio || 1) * 100) / 100,
        };
    } catch { return { w: 0, h: 0, dpr: 1 }; }
}

function _timezone() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; }
    catch { return ""; }
}

function _captureUtm() {
    try {
        const url = new URL(window.location.href);
        const utm = {
            utm_source: (url.searchParams.get("utm_source") || "").slice(0, 80),
            utm_medium: (url.searchParams.get("utm_medium") || "").slice(0, 80),
            utm_campaign: (url.searchParams.get("utm_campaign") || "").slice(0, 120),
            utm_content: (url.searchParams.get("utm_content") || "").slice(0, 120),
            utm_term: (url.searchParams.get("utm_term") || "").slice(0, 120),
            gclid: (url.searchParams.get("gclid") || "").slice(0, 120),
            fbclid: (url.searchParams.get("fbclid") || "").slice(0, 120),
        };
        const any = Object.values(utm).some((v) => v);
        if (any) {
            sessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
            return utm;
        }
        const stored = sessionStorage.getItem(UTM_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch { return {}; }
}

function _originPage() {
    // The very first internal page a user landed on inside this session — used
    // to answer "which page originally sent this donor to /donate?".
    try {
        let o = sessionStorage.getItem(ORIGIN_KEY);
        if (!o) {
            o = (window.location.pathname || "") + (window.location.search || "");
            sessionStorage.setItem(ORIGIN_KEY, o.slice(0, 300));
        }
        return o;
    } catch { return ""; }
}

// Initialize UTM + origin as soon as the module loads (client-only).
if (typeof window !== "undefined") {
    try { _captureUtm(); _originPage(); } catch { /* swallow */ }
}

export function trackEvent(event, data = {}) {
    try {
        const payload = {
            event,
            data: data || {},
            session_id: _sessionId(),
            session_age_ms: _sessionAgeMs(),
            device: _deviceType(),
            browser: _browser(),
            os: _os(),
            viewport: _viewport(),
            timezone: _timezone(),
            path: typeof window !== "undefined" ? window.location.pathname : "",
            origin_page: _originPage(),
            referrer: typeof document !== "undefined" ? (document.referrer || "") : "",
            utm: _captureUtm(),
            ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
            at: new Date().toISOString(),
        };
        // Fire and forget — never await, never surface errors to the caller.
        api.post("/analytics/event", payload).catch(() => {});
    } catch { /* swallow */ }
}

/** Convenience: emit a GA4 event too, if gtag is present (added by AnalyticsLoader). */
export function trackGtag(event, params = {}) {
    try {
        if (typeof window !== "undefined" && typeof window.gtag === "function") {
            window.gtag("event", event, params);
        }
    } catch { /* swallow */ }
}
